/**
 * useAvailabilitySelection — the flow-agnostic core of the booking page's first
 * step: date → time slots → bay, with both responsive branches.
 *
 * Extracted VERBATIM from `BookingTimeStep` (S3) so the demo flow can mirror
 * the page without forking a thousand lines: the responsive machinery
 * (`isMobile` + the mobile time/bay substep machine), the two availability
 * queries with their debounce and club-timezone ISO conversion, duration
 * derivation, bay selection (tap + sole-bay auto-select), and every handler
 * that drives them. What stays per-flow in the step components: validation,
 * club access, booking creation, CTAs, and all rendering.
 *
 * Visual-step wiring is the one indirection: the extracted logic fires
 * `onPhaseChange` at exactly the points the standard step used to call
 * `setVisualStep`, and each flow maps phases to its own stepper. The
 * time-selected notification deliberately stays an EFFECT (not an inline call
 * in `handleSlotsSelect`), preserving the standard flow's edit-a-selected-bay
 * behavior.
 *
 * Flow branches (both no-ops for `flow: 'standard'`): a fixed demo duration
 * replaces the duration-dropdown derivation, and the subscription lookup is
 * skipped — a demo resolves no subscription.
 *
 * @module pages/booking/shared/useAvailabilitySelection
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { buildSelectedSlots, getAvailableDurations, type TimeSlot, type SelectionRange } from '../components/TimeSlotSelector';
import type { ApiAvailableBay, BayAvailabilityItem } from '@lgc/database/types';
import { useBooking } from '~/services/internalApi/booking';
import { useActiveSubscription } from '~/services/internalApi/subscription';
import { useClubQuery } from '~/services/internalApi/club/hooks/useClubQuery';
import { useAvailableBaysQuery, useClubAvailabilityQuery } from '~/services/internalApi/bay';
import { useAuth } from '~/services/auth';
import { butterToast } from '@butter/ui/patterns';
import { getOperatingHoursForDate } from '~/services/internalApi/club/utils';
import { getValidDurations, isStartTooSoon, minNoticeRefusal, resolveAdvanceBookingDays, START_PASSED_REFUSAL } from '@butter/library/availability';
import { addBreadcrumb, setTenantContext } from '@butter/monitoring';
import {
  timeStringToMinutes,
  minutesToTimeString,
  createClubDateTimeFromSlot,
  extractLocalDateISO,
} from '@butter/library/datetime';
import { clubMaxBookableDate, clubTodayAsPickerDate } from '../steps/clubCalendarBounds';
import { useDebounce } from '~/hooks/useDebounce';
import { toNumericUserId } from '~/lib/utils/userIdUtils';

/**
 * Module scope so the no-selection case is referentially stable — see
 * `docs/conventions/react-state.md`. A bare `[]` would be a new array every render,
 * which defeats every downstream memo and dependency array that reads it.
 */
const EMPTY_SLOTS: number[] = [];
const EMPTY_BAYS: { id: number; name: string; isActive: boolean }[] = [];

/** Where the selection stands — each flow maps these onto its own stepper. */
export type AvailabilityPhase = 'time' | 'bay';

export interface AvailabilitySelectionConfig {
  flow: 'standard' | 'demo';
  /**
   * Demo: the club's `demoBookingConfig.sessionDurationMinutes` — the ONE
   * length a demo may be, so the duration dropdown's derivation is bypassed.
   * Standard: omit.
   */
  fixedDurationMinutes?: number;
  /**
   * Fired where the standard step used to call `setVisualStep`: `'time'` on
   * mount and whenever the selection is cleared, `'bay'` (from an effect) when
   * a time is chosen and no bay is, and `'bay'` again when the mobile
   * "Select a Bay" button advances the substep. Keep the callback stable
   * (useCallback) — it sits in effect deps.
   */
  onPhaseChange?: (phase: AvailabilityPhase) => void;
}

export function useAvailabilitySelection(config: AvailabilitySelectionConfig) {
  const { flow, fixedDurationMinutes, onPhaseChange } = config;

  // Mobile substep state ('time' or 'bay')
  const [mobileSubstep, setMobileSubstep] = useState<'time' | 'bay'>('time');

  // Responsive detection
  const [isMobile, setIsMobile] = useState(false);

  // Track if this is the initial mount to set the correct visual step
  const isInitialMount = useRef(true);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768); // md breakpoint
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Get booking state from context
  const {
    selectedDate,
    setSelectedDate,
    selectedStartTime,
    selectedEndTime,
    setSelectedStartTime,
    setSelectedEndTime,
    selectedBayId,
    setSelectedBayId,
    guestCount,
    setGuestCount,
    clearBooking,
    setAvailableBays,
  } = useBooking();

  // Local state for filters (not persisted in context)
  const [selectedDuration, setSelectedDuration] = useState(60);
  const [bayFilterId, setBayFilterId] = useState<number | null>(null); // Separate filter state

  // Set initial visual step when component mounts
  // Always reset to the flow's first phase since this hook backs step 0
  useEffect(() => {
    if (isInitialMount.current) {
      onPhaseChange?.('time');
      isInitialMount.current = false;
    }
  }, [onPhaseChange]);

  // Fetch club data (includes pricing, operating hours, bays).
  // Loading/error are handled centrally by ClubGate (mounted in MainLayout),
  // so callers can trust that club data is present when they render.
  const { data: club } = useClubQuery();
  // The flow's minimum notice, for re-checking a grid fetched a while ago (the grid
  // and the slot pick both read it). Standard only: a demo's notice is its own,
  // which the server applies, and a lesson has none.
  const noticeMinutes = flow === 'standard' ? (club?.minNoticeMinutes ?? 0) : 0;
  // Hoisted so the memoized converters below can depend on exactly this value
  // (the React Compiler rejects deps narrower or wider than what it infers).
  const clubTimezone = club?.timezone;

  // selectedDate is already a DateString (YYYY-MM-DD) — extracted at the DatePicker
  // boundary in handleDateChange, so no conversion needed here.
  const formattedDate = selectedDate;

  // Derive duration options from club constraint columns — mode-independent.
  // Clamp slotIntervalMinutes to a minimum of 30 for the web portal: we do not
  // expose 15-minute booking increments in the UI even when the club DB value is 15.
  const effectiveSlotInterval = Math.max(club?.slotIntervalMinutes ?? 30, 30);

  const durationOptions = useMemo(() => {
    // A fixed-length flow (a demo) books its own length: the club's minimum and
    // maximum session bind standard bookings only, as on the server and on mobile.
    // Building this list from the club for a demo filtered a 30-minute demo out at a
    // club with a 60-minute minimum, and the length check below then cleared every
    // slot the member picked.
    if (fixedDurationMinutes) return [fixedDurationMinutes];
    if (!club?.maxSessionMinutes || !club?.slotIntervalMinutes) return [];
    // The club's minimum session is the floor — the server refuses anything under
    // it, so the picker never offers it.
    return getValidDurations(club.maxSessionMinutes, effectiveSlotInterval, club.minSessionMinutes ?? null);
  }, [club, effectiveSlotInterval, fixedDurationMinutes]);

  // The length of the current selection, when there is one. A selection restored
  // from sessionStorage after a reload arrives with its start and end but not the
  // duration state (that is local, and starts at 60), so without this a 90-minute
  // selection would be filtered and length-checked as 60 — and could be cleared as
  // "no longer available". While a selection exists, its length is the duration:
  // handleDurationChange moves the end time whenever the length changes.
  const selectionMinutes = useMemo(() => {
    if (!selectedStartTime || !selectedEndTime) return null;
    const start = timeStringToMinutes(selectedStartTime);
    const end = timeStringToMinutes(selectedEndTime);
    return start === null || end === null ? null : end - start;
  }, [selectedStartTime, selectedEndTime]);

  // Effective duration: a demo's is fixed by club config; otherwise the selection's
  // own length, then the stored value if it's a valid option, falling back to the
  // first configured option. Derived rather than synced, so there is no
  // setState-in-effect cascade.
  const effectiveSelectedDuration = fixedDurationMinutes
    ?? (selectionMinutes !== null && durationOptions.includes(selectionMinutes)
      ? selectionMinutes
      : durationOptions.includes(selectedDuration)
        ? selectedDuration
        : (durationOptions[0] ?? 60));

  // Determine bayId for availability query
  // - If specific bay filter selected (not null), use that bayId
  // - If null (all bays), pass undefined to get aggregated availability
  const bayIdForQuery = bayFilterId || undefined;

  // Get auth state
  const { isAuthenticated, user: authUser } = useAuth();

  // Get numeric user ID for API calls (needed early for availability query)
  const numericUserId = useMemo(() => {
    if (!authUser?.id) return undefined;
    return toNumericUserId(authUser.id);
  }, [authUser]);

  // Fetch user's active subscription to check for guest fee overrides.
  // Demo flow: skipped — a demo resolves no subscription (D14).
  const { activeSubscription } = useActiveSubscription({
    userId: numericUserId,
    enabled: isAuthenticated && flow === 'standard',
  });
  const isTeamBooking = activeSubscription?.type === 'membership' && activeSubscription.teamId != null;

  /**
   * Resolved guest fee config.
   *
   * Subscribed users: `guestFeePolicy` is the SERVER-resolved chain
   * (instance → template → club, team plans included) — quoting from it means
   * the picker shows exactly what booking creation will charge. Walk-ins
   * (no subscription): club defaults, matching the server's null-overrides path.
   */
  const resolvedGuestFeeConfig = useMemo(() => {
    const policy = activeSubscription?.guestFeePolicy;
    if (policy) {
      return {
        enabled: policy.enabled,
        freeGuests: policy.freeGuestsPerBooking,
        feePerGuestCents: policy.feePerGuest,
      };
    }

    return {
      enabled: club?.guestFeesEnabled ?? false,
      freeGuests: club?.guestFeeConfig?.freeGuestsPerBooking ?? 0,
      feePerGuestCents: club?.guestFeeConfig?.feePerGuest ?? 0,
    };
  }, [club?.guestFeesEnabled, club?.guestFeeConfig, activeSubscription]);

  // What the client asks the API for: EVERY duration option, in one request.
  //
  // The API emits one slot per (start, length) pair and aggregates on that pair, so a
  // response for `[30,60,90,120]` strictly CONTAINS the response for `[60]` — proven in
  // `apps/api/.../__tests__/unit/durationContainment.test.ts`. That means one request
  // serves both consumers, and it is why there is no second "probe" query here any more:
  //
  //   • the GRID gets `gridSlots` — this response filtered to the selected length. It
  //     must never see the raw mixed-length list; see the invariant on `gridSlots`.
  //   • the LENGTH GATE gets the raw list, so it can say which lengths the club can
  //     actually serve from the chosen start.
  //
  // The lengths themselves are still BOOKING lengths, not grid steps. Club-wide
  // availability is an OR across bays computed per slot, so cells one interval long only
  // answer "is some bay free for these 30 minutes"; a 90-min booking stitched from three
  // of them could take a different bay in each, and the bay step — which asks per bay
  // about the whole range — then returns none. `slotInterval` is what steps the grid, so
  // cells stay 30 minutes apart and only their LENGTH varies.
  //
  // `durations` is part of the query key, and it is now CONSTANT for a given club. That
  // is the payoff: changing the booking length no longer changes the key, so it costs
  // zero requests and shows no spinner.
  //
  // Demo sends the same list and the server ignores it — `type=demo` hard-sets the grid
  // to `demoBookingConfig.sessionDurationMinutes` — so `gridSlots` is a pass-through
  // there. The single-option and unconfigured-club cases fall back to the one length so
  // the request shape does not change for them.
  const requestedDurations =
    !fixedDurationMinutes && durationOptions.length > 1
      ? durationOptions.join(',')
      : String(effectiveSelectedDuration);

  // Use the unified club availability query
  // - bayId undefined = aggregated across all bays
  // - bayId number = specific bay only
  // - userId = used to check subscription status for subscription-only clubs
  // Shared by the grid query and the club-wide query below; only `bayId` differs.
  const availabilityQueryParams = {
    clubId: club?.id || 0,
    date: formattedDate,
    userId: numericUserId, // Pass userId for subscription check
    durations: requestedDurations,
    // Override the backend's bookingInterval so time slots step at 30-min minimum
    // even when the club's slotIntervalMinutes is set to 15.
    slotInterval: effectiveSlotInterval,
    // Demo flow: the grid comes from the club's demo window (also part of the
    // query key, so demo and standard never cross-serve from cache).
    type: flow === 'demo' ? ('demo' as const) : undefined,
    enabled: !!club && !!formattedDate,
  };

  const {
    data: availability,
    isLoading: availabilityLoading,
    isPlaceholderData: availabilityIsStale,
    error: availabilityError,
  } = useClubAvailabilityQuery({ ...availabilityQueryParams, bayId: bayIdForQuery });

  // The restricted-bay list rides only on the club-wide response. With no bay filter
  // this is the grid query's own key (no extra request); with one it is the cached
  // aggregate, which is what keeps the dropdown from re-offering a bay just filtered out.
  const { data: clubWideAvailability } = useClubAvailabilityQuery({
    ...availabilityQueryParams,
    bayId: undefined,
  });

  // The grid's slots: exactly one `durationMinutes` value, always.
  //
  // ⚠️ INVARIANT — `TimeSlotSelector` must receive a single-length list. `canFitDuration`
  // and `getConsecutiveSlots` both look slots up with `find(s => s.startMinutes === …)`,
  // matching on START TIME ALONE, so a mixed-length list makes them answer with whichever
  // length the server happened to emit first. That is not a render bug: `handleSlotsSelect`
  // computes the booked end from `lastSlot.durationMinutes`, so a wrong-length slot writes
  // a wrong-length BOOKING into context. A mixed list also duplicates React keys and
  // `time-slot-button-<start>` testids. Filter here, once — never inside the components.
  const gridSlots = useMemo(
    () => (availability?.slots ?? []).filter((s) => s.durationMinutes === effectiveSelectedDuration),
    [availability, effectiveSelectedDuration],
  );

  // Check if user has selected a time range
  const hasTimeSelection = selectedStartTime !== null && selectedEndTime !== null;
  const hasBaySelection = selectedBayId !== null;

  // Update visual step when time is selected (both mobile and desktop)
  // This only updates the progress indicator - NO navigation happens here
  // Desktop: Bay section appears via CSS progressive disclosure (data-visible attribute)
  // Mobile: User must click "Select a Bay" button to see bay section
  //
  // Deliberately an EFFECT, not an inline call in handleSlotsSelect — the
  // caller's gate (e.g. the standard flow's currentStep check) runs on the
  // fresh value each time, and editing a selected bay keeps its behavior.
  useEffect(() => {
    if (hasTimeSelection && !hasBaySelection) {
      // Update visual step to show bay selection in progress indicator
      onPhaseChange?.('bay');
    }
  }, [hasTimeSelection, hasBaySelection, onPhaseChange]);

  // Mobile: Navigate to BAY_SELECTION step when "Select a Bay" is clicked
  const handleSelectBayClick = () => {
    setMobileSubstep('bay');
    onPhaseChange?.('bay');
    // No logical-step navigation — mobile substeps are just UI state changes
  };

  // Mobile: Go back to TIME_SELECTION step when "Edit" is clicked
  const handleEditTimeClick = () => {
    setMobileSubstep('time');
    onPhaseChange?.('time');
    // Don't navigate back in context — just show the time substep view again
  };

  // Debounce time selection to prevent excessive API calls while user is selecting slots
  const debouncedStartTime = useDebounce(selectedStartTime, 300);
  const debouncedEndTime = useDebounce(selectedEndTime, 300);
  const hasDebouncedTimeSelection = debouncedStartTime !== null && debouncedEndTime !== null;

  // Get minimum selectable date (today in club's timezone)
  const getMinDate = (): Date => clubTodayAsPickerDate(club?.timezone);

  // Get maximum selectable date — today + resolved advance booking window.
  // Override chain: subscription instance → subscription plan template → club default → 14
  // (a demo's subscription values are always null, leaving the club default).
  const getMaxDate = (): Date =>
    clubMaxBookableDate(
      club?.timezone,
      resolveAdvanceBookingDays(
        club?.advanceBookingDays ?? 14,
        activeSubscription?.plan?.advanceBookingDays ?? null,
        activeSubscription?.advanceBookingDays ?? null,
      ),
    );

  // Helper: Convert club date/time to UTC ISO string for validation API
  const createUTCTimestamp = useMemo(() => {
    return (timeString: string): string | null => {
      if (!clubTimezone || !selectedDate) {
        return null;
      }

      try {
        // createClubDateTimeFromSlot handles the "24:00" end-of-day sentinel
        // (advancing to next-day 00:00) so we don't re-derive it here.
        const result = createClubDateTimeFromSlot(selectedDate, timeString, clubTimezone);
        return result.date.toISOString();
      } catch (error) {
        console.error('[useAvailabilitySelection] Failed to create UTC timestamp:', error);
        return null;
      }
    };
  }, [selectedDate, clubTimezone]);

  // Build ISO timestamps for validation / creation APIs
  const startTimeISO = useMemo(() =>
    debouncedStartTime ? createUTCTimestamp(debouncedStartTime) : null,
    [debouncedStartTime, createUTCTimestamp]
  );

  const endTimeISO = useMemo(() =>
    debouncedEndTime ? createUTCTimestamp(debouncedEndTime) : null,
    [debouncedEndTime, createUTCTimestamp]
  );

  // Derive member status from the availability response.
  // The availability endpoint fires as soon as club + date are known (before bay selection),
  // so this is set early enough to correctly gate membership-only time slots.
  const isMember = availability?.userSubscriptionType === 'membership';

  // Extract booking restriction from availability API response
  // This tells us if the user can book (false when subscription required but user doesn't have one)
  const userCanBook = availability?.userCanBook ?? true; // Default to true if not set

  // Format datetime for available bays API using debounced time
  // Convert club local time to UTC ISO string (industry standard)
  const baysStartTime = useMemo(() => {
    if (!debouncedStartTime || !clubTimezone) return undefined;

    // FromSlot, not the plain call: `selectedStartTime` can legitimately be the
    // "24:00" end-of-day sentinel, which only the slot-aware converter resolves
    // correctly. `BookingContext` screens restored values, but this is a bare
    // useMemo — a throw here renders as a white screen, so it takes the
    // converter that cannot throw on a value the app itself produces.
    const result = createClubDateTimeFromSlot(
      formattedDate,
      debouncedStartTime,
      clubTimezone
    );
    return result.date.toISOString(); // Returns "2026-02-03T20:00:00.000Z"
  }, [formattedDate, debouncedStartTime, clubTimezone]);

  const baysEndTime = useMemo(() => {
    if (!debouncedEndTime || !clubTimezone) return undefined;

    // createClubDateTimeFromSlot handles the "24:00" end-of-day sentinel.
    const result = createClubDateTimeFromSlot(formattedDate, debouncedEndTime, clubTimezone);
    return result.date.toISOString();
  }, [formattedDate, debouncedEndTime, clubTimezone]);

  // Fetch available bays for the selected date and time with duration-based checking
  const {
    data: availableBaysResult,
    isLoading: baysLoading,
    // The bays query carries `placeholderData: keepPreviousData`, so on a range change
    // `data` is the PREVIOUS range's answer and `isLoading` stays false. Anything that
    // decides whether the member may proceed has to know that.
    isPlaceholderData: baysAreStale,
    error: baysError,
  } = useAvailableBaysQuery({
    clubId: club?.id || 0,
    startTime: baysStartTime,
    endTime: baysEndTime,
    userId: numericUserId,
    collectAllRestrictions: false,
    // Pass bay filter to backend for server-side filtering
    bayIds: bayFilterId ? [bayFilterId] : undefined,
    // Communicate the effective slot interval to the API (always 30 min minimum in web portal)
    slotIntervalMinutes: effectiveSlotInterval,
    type: flow === 'demo' ? 'demo' : undefined,
    enabled: !!club && !!baysStartTime && !!baysEndTime && hasDebouncedTimeSelection,
  });

  // Extract payload from ApiResult
  const availableBays = availableBaysResult?.success ? availableBaysResult.payload : null;

  // Log availability data for debugging.
  //
  // Counts are `gridSlots`, NOT the raw response. The request now asks for every duration
  // at once, so `availability.slots` holds one entry per (start, length) pair — up to 8×
  // the number of start times. Reporting that would make old and new Sentry traces
  // incomparable and read as "availability tripled". `gridSlots` keeps the number meaning
  // what it has always meant: bookable start times at the selected length.
  useEffect(() => {
    if (availability && import.meta.env.DEV) {
      console.log('📅 [useAvailabilitySelection] Availability data loaded:', {
        timezone: availability.timezone,
        date: availability.date,
        slotsCount: gridSlots.length,
        availableSlots: gridSlots.filter(s => s.isAvailable).length,
        bookingRestriction: availability.bookingRestriction,
      });
    }

    if (availability && availability.slots) {
      addBreadcrumb('booking', 'Availability data loaded', {
        date: availability.date,
        timezone: availability.timezone,
        totalSlots: gridSlots.length,
        availableSlots: gridSlots.filter(s => s.isAvailable).length,
      });
    }
  }, [availability, gridSlots]);

  // Log available bays data when loaded. Console output is DEV-only (PR #1375
  // nit 5 — this used to print every render pass in production); the
  // breadcrumb stays unconditional, matching the availability effect above.
  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('🔍 [useAvailabilitySelection] Bay query state:', {
        hasResult: !!availableBaysResult,
        success: availableBaysResult?.success,
        hasItems: !!availableBays?.items,
        itemsLength: availableBays?.items?.length,
        baysLoading,
        baysError: baysError?.message,
        hasDebouncedTimeSelection,
        baysStartTime,
        baysEndTime,
      });
    }

    if (availableBays?.items) {
      if (import.meta.env.DEV) {
        console.log('🏌️ [useAvailabilitySelection] Available bays loaded:', {
          bays: availableBays.items,
          total: availableBays.total,
          available: availableBays.available,
        });
      }

      addBreadcrumb('booking', 'Available bays loaded', {
        total: availableBays.total,
        available: availableBays.available,
      });
    }
  }, [availableBaysResult, availableBays, baysLoading, baysError, hasDebouncedTimeSelection, baysStartTime, baysEndTime]);

  // Store available bays in context to prevent duplicate API calls from child components
  useEffect(() => {
    if (availableBays) {
      setAvailableBays(availableBays);
    }
  }, [availableBays, setAvailableBays]);

  // The selected bay, resolved from the id in context against the bays currently
  // on offer.
  //
  // Derived rather than mirrored in local state, for three reasons: `selectedBayId`
  // is what later steps actually book with, so a second copy here could only ever
  // disagree with it; a selection that survives into a refetch where the bay is no
  // longer bookable becomes null on its own, which is what every
  // `selectedBay?.isAvailable` gate wants; and it keeps the auto-selection effect
  // writing context setters only.
  const selectedBay: ApiAvailableBay | null = useMemo(() => {
    const bay = availableBays?.items.find(b => b.id === selectedBayId);
    if (!bay) return null;
    // API returns full bay details — widen to the ApiAvailableBay callers read.
    return {
      ...bay,
      status: bay.isAvailable ? 'available' : 'unavailable',
      statusMessage: bay.restrictions[0]?.message || (bay.isAvailable ? 'Available' : 'Unavailable'),
      availabilityState: 'PUBLIC',
      nextAvailableTime: null,
      availableFrom: null,
      availableTo: null,
      clubClosed: bay.restrictions.some(r => r.type === 'CLUB_CLOSED'),
    };
  }, [availableBays, selectedBayId]);

  // Adopt a bay as the selection. Shared by the explicit tap and the sole-bay
  // effect below so both populate context, clamp guests and leave a breadcrumb
  // identically — rather than one path quietly doing less than the other.
  const selectBay = useCallback((bay: BayAvailabilityItem, reason: string) => {
    setSelectedBayId(bay.id);

    // Clamp guest count to what this bay can seat, in case the member previously
    // chose more than it allows.
    //
    // `capacity - 1`, not `capacity`: capacity counts the booker, which is the
    // bound `GuestSelector` renders and the one `createBooking` enforces
    // (`maxGuests = bay.capacity - 1`, refused as GUEST_COUNT_EXCEEDS_CAPACITY).
    const maxGuestsForBay = Math.max(0, bay.capacity - 1);
    if (guestCount > maxGuestsForBay) {
      setGuestCount(maxGuestsForBay);
    }

    addBreadcrumb('booking', reason, {
      bayId: bay.id,
      bayName: bay.name,
      isAvailable: bay.isAvailable,
    });
  }, [guestCount, setGuestCount, setSelectedBayId]);

  // Exactly one bookable bay — there is nothing to choose, so choose it.
  //
  // Reduced to an id before it reaches the deps (`items` is a fresh array every
  // fetch), and guarded on there being no selection yet, so a member's own pick
  // is never overwritten and this settles after one run.
  const soleBookableBayId = useMemo(() => {
    const bookable = (availableBays?.items ?? []).filter(b => b.isAvailable);
    return bookable.length === 1 ? bookable[0].id : null;
  }, [availableBays]);

  useEffect(() => {
    // Guard on the RESOLVED selection, not the raw id: an id left over from a
    // previous time slot resolves to null against the bays now on offer, and
    // that member should still get the sole bay picked for them.
    if (soleBookableBayId === null || selectedBay !== null) return;
    const bay = availableBays?.items.find(b => b.id === soleBookableBayId);
    if (bay) selectBay(bay, 'Bay auto-selected (only one available)');
  }, [soleBookableBayId, selectedBay, availableBays, selectBay]);

  // Set tenant context when club data loads (for monitoring)
  useEffect(() => {
    if (club) {
      setTenantContext({
        clubId: club.id,
        clubName: club.name,
        tenantId: club.tenant.id,
        tenantHandle: club.tenant.handle
      });
    }
  }, [club]);

  // Map bays to TimeSlotSelector format.
  //
  // Memoized because it escapes this hook as the `bays` prop — a bare `.map()` here is
  // a new array every render, which defeats any downstream memo or dependency array
  // that reads it (`docs/conventions/react-state.md`).
  //
  // A bay the session cannot book is not offered as a filter. Until the club-wide
  // answer has arrived, nothing is offered: an absent answer must not read as "no
  // bay is restricted". Keyed on the joined ids, not the array, so a new response
  // with the same set does not rebuild the list.
  const restrictedBayIdsKey = clubWideAvailability?.restrictedBayIds?.join(',') ?? '';
  const hasClubWideAnswer = clubWideAvailability !== undefined;
  const baysForSelector = useMemo(() => {
    if (!hasClubWideAnswer) return EMPTY_BAYS;
    const restricted = new Set(restrictedBayIdsKey ? restrictedBayIdsKey.split(',').map(Number) : []);
    return (club?.bays ?? [])
      .filter(bay => !restricted.has(bay.id))
      .map(bay => ({
        id: bay.id,
        name: bay.name,
        isActive: true,
      }));
  }, [club, hasClubWideAnswer, restrictedBayIdsKey]);

  // Get operating hours for selected date (null until the club is loaded —
  // callers guard on club before reading this)
  const operatingHours = club?.publicHours
    ? getOperatingHoursForDate(
        formattedDate,
        club.publicHours,
        club.membershipHours || undefined,
        club.scheduleExceptions || undefined,
        isMember
      )
    : null;

  /**
   * Empty the time selection — every part of it.
   *
   * Three paths clear a selection and they used to clear different subsets: the bay id,
   * the visual phase and the mobile substep were each dropped by some but not all. That
   * left reachable states like "no time selected, still on the mobile bay substep,
   * looking at a bay list whose query has just been disabled". One definition, three
   * callers, no half-cleared state.
   */
  const clearTimeSelection = useCallback(() => {
    setSelectedStartTime(null);
    setSelectedEndTime(null);
    setSelectedBayId(null);
    onPhaseChange?.('time');
  }, [setSelectedStartTime, setSelectedEndTime, setSelectedBayId, onPhaseChange]);

  const handleSlotsSelect = (slots: TimeSlot[]) => {
    if (slots.length === 0) {
      // Keep the length the member was looking at. After a reload it came from the
      // restored selection, not `selectedDuration`, and would fall back to 60 here.
      setSelectedDuration(effectiveSelectedDuration);
      clearTimeSelection();
      // Reset the STORED substep too, so picking a new time lands back on the time view
      // rather than jumping straight to bays. Safe here — an event handler, not an effect.
      if (isMobile) setMobileSubstep('time');
      return;
    }

    const firstSlot = slots[0];
    const startTimeStr = minutesToTimeString(firstSlot.startMinutes);

    const lastSlot = slots[slots.length - 1];
    const endMinutes = lastSlot.startMinutes + (lastSlot.durationMinutes ?? 30);
    const endTimeStr = minutesToTimeString(endMinutes);

    // Validate that the selected start has not passed — beyond the club's grace,
    // which the grid already honours (a 3:01 booker is offered the 3:00 slot).
    if (club?.timezone && startTimeStr && endTimeStr) {
      try {
        const selectedDateTime = createClubDateTimeFromSlot(
          formattedDate,
          startTimeStr,
          club.timezone
        );

        // Compared as true UTC instants (BUG-001: never against a ZonedDate).
        const graceMinutes = club.bookingGraceMinutes ?? 0;
        if (isStartTooSoon(selectedDateTime.date, { graceMinutes })) {
          // `error`, not `info`: the booking is blocked and the member has to pick
          // another slot. There is no `warning` variant.
          butterToast.error(START_PASSED_REFUSAL);
          console.warn('[useAvailabilitySelection] Selected past time:', {
            selectedTime: selectedDateTime.date,
            utcNow: new Date().toISOString(),
          });
          return;
        }
        // Not started, but inside the club's notice — a grid left open drifts into
        // it. Same sentence the API would send.
        if (noticeMinutes > 0 && isStartTooSoon(selectedDateTime.date, { noticeMinutes, graceMinutes })) {
          butterToast.error(minNoticeRefusal(noticeMinutes));
          return;
        }
      } catch (error) {
        console.error('[useAvailabilitySelection] Failed to validate time:', error);
      }
    }

    addBreadcrumb('booking', 'Time slots selected', {
      slotsCount: slots.length,
      startTime: startTimeStr,
      endTime: endTimeStr,
      durationMinutes: endMinutes - firstSlot.startMinutes
    });

    setSelectedStartTime(startTimeStr);
    setSelectedEndTime(endTimeStr);
  };

  const handleBayChange = (newBayId: number | 'all') => {
    const bayId = newBayId === 'all' ? null : newBayId;

    addBreadcrumb('booking', 'Bay filter changed', {
      bayId: newBayId,
      bayName: newBayId === 'all' ? 'All Bays' : baysForSelector.find(b => b.id === newBayId)?.name
    });

    setBayFilterId(bayId);
  };

  const handleBaySelect = (bayId: number) => {
    const bay = availableBays?.items.find(b => b.id === bayId);
    if (bay) selectBay(bay, 'Bay selected from carousel');
  };

  const handleDateChange = (date: Date) => {
    const dateStr = extractLocalDateISO(date);
    addBreadcrumb('booking', 'Date selected', {
      date: dateStr,
      dayOfWeek: date.toLocaleDateString('en-US', { weekday: 'long' }),
    });

    setSelectedDate(dateStr);
  };

  // Which grid cells the selection covers — drives the highlighted start
  // buttons only. The grid steps by the interval, so a booking covers
  // `duration / interval` cells; the reported end time comes from
  // `selectedRange` below, not from the last cell — that separation is what
  // makes the multi-cell highlight safe.
  //
  // Both are memoized, and `EMPTY_SLOTS` is module scope, because
  // `docs/conventions/react-state.md` — "a hook must not return a freshly-allocated
  // array or object". Both escape this hook as props, and both now sit in dependency
  // arrays below. A bare literal here is the same shape that took checkout down
  // (React #301, see `useWaivers.test.tsx`); memoizing the derived half while leaving
  // a bare `[]` for the empty half fixes nothing, so both halves are handled.
  const selectedRange: SelectionRange | null = useMemo(() => {
    if (!selectedStartTime || !selectedEndTime) return null;
    const startMinutes = timeStringToMinutes(selectedStartTime);
    const endMinutes = timeStringToMinutes(selectedEndTime);
    if (startMinutes === null || endMinutes === null) return null;
    return { startMinutes, endMinutes };
  }, [selectedStartTime, selectedEndTime]);

  const selectedSlots: number[] = useMemo(
    () =>
      selectedRange
        ? buildSelectedSlots(selectedRange.startMinutes, selectedRange.endMinutes, effectiveSlotInterval)
        : EMPTY_SLOTS,
    [selectedRange, effectiveSlotInterval],
  );

  // Which lengths the club can actually serve from the chosen start.
  //
  // Reads the RAW response, not `gridSlots` — this is the one consumer that wants every
  // (start, length) pair. Filtering first would leave only the selected length and the
  // answer would be "the length you already picked", which is useless.
  //
  // On the FIRST fetch `slots` is empty, so this returns everything enabled — the
  // alternative reads as "nothing is bookable". After that, `placeholderData:
  // keepPreviousData` serves the PREVIOUS query's slots for one beat on a date/bay
  // change, so the gate briefly answers for the previous date. That is why the
  // clear-on-unavailable effect below waits for `!availabilityIsStale`.
  // Memoized because it sits in the clear-on-unavailable effect's deps below:
  // `getAvailableDurations` returns a fresh `.filter()` array whenever a start is
  // selected, so without this the effect re-runs on every render of the step.
  const availableDurations = useMemo(
    () =>
      getAvailableDurations(
        selectedRange?.startMinutes ?? null,
        availability?.slots ?? [],
        durationOptions,
        isMember,
      ),
    [selectedRange?.startMinutes, availability, durationOptions, isMember],
  );

  // Changing the booking length KEEPS the start and re-ends the booking. One
  // behaviour at both widths — the mobile sheet and the desktop dropdown call this.
  //
  // It used to clear both endpoints and make the member re-pick, and the mobile sheet
  // had a separate `handleDurationExtend` that kept the start. That split was only
  // safe on mobile because the sheet disables lengths the backend refuses; the desktop
  // dropdown had no such gate. It does now (`availableDurations` reaches
  // `TimeSlotFilters`), so the two paths collapsed into this one.
  //
  // Declared here rather than beside the other handlers because it reads
  // `selectedRange`, which is derived further down.
  const handleDurationChange = (duration: number) => {
    setSelectedDuration(duration);
    if (!selectedRange) return;
    const endMinutes = selectedRange.startMinutes + duration;
    // `minutesToTimeString` throws above 1440, and the stored end time has no
    // past-midnight form, so a length that runs past 24:00 cannot be held here.
    // Fall back to clear-and-repick rather than writing an unrepresentable end.
    if (endMinutes > 1440) {
      clearTimeSelection();
      return;
    }
    setSelectedEndTime(minutesToTimeString(endMinutes));
  };

  // The member's chosen length stopped being servable from their chosen start — most
  // often because they moved to a date where it no longer fits. Clear the selection and
  // say so, rather than leaving a length selected that the grid can no longer offer.
  //
  // Gated on `!availabilityIsStale`: `keepPreviousData` serves the PREVIOUS date's slots
  // for one beat after a date/bay change, so without this the effect would fire against
  // the old date and clear a selection that is perfectly valid on the new one.
  //
  // `butterToast` has no `warning` variant (info | success | error) — `error` is the
  // right one here for the same reason `handleSlotsSelect` picks it: the booking is
  // blocked and the member has to act.
  useEffect(() => {
    if (availabilityIsStale) return;
    if (!hasTimeSelection) return;
    if (durationOptions.length === 0) return;
    if (availableDurations.includes(effectiveSelectedDuration)) return;

    clearTimeSelection();
    butterToast.error('That booking length is no longer available at the time you picked. Please choose again.');
  }, [
    availabilityIsStale,
    hasTimeSelection,
    durationOptions.length,
    availableDurations,
    effectiveSelectedDuration,
    clearTimeSelection,
  ]);

  // You cannot be picking a bay with no time selected, so derive that rather than syncing
  // it. This is what lets `clearTimeSelection` stay free of local setState — the
  // clear-on-unavailable EFFECT calls it, and `set-state-in-effect` (correctly) rejects a
  // synchronous local write there. Clearing the time now returns the mobile view to the
  // grid on its own, with no cascading render.
  const effectiveMobileSubstep = hasTimeSelection ? mobileSubstep : 'time';

  return {
    // Club + auth
    club,
    noticeMinutes,
    isAuthenticated,
    authUser,
    numericUserId,
    // Responsive
    isMobile,
    mobileSubstep: effectiveMobileSubstep,
    // Selection state (from BookingContext)
    selectedDate,
    selectedStartTime,
    selectedEndTime,
    selectedBayId,
    hasTimeSelection,
    hasBaySelection,
    selectedSlots,
    selectedRange,
    guestCount,
    setGuestCount,
    clearBooking,
    // Filters
    bayFilterId,
    // Durations / grid
    durationOptions,
    availableDurations,
    effectiveSelectedDuration,
    effectiveSlotInterval,
    // Availability query
    availability,
    /** The grid's slots — single-length. Pass THIS to TimeSlotSelector, never `availability.slots`. */
    gridSlots,
    availabilityLoading,
    availabilityIsStale,
    availabilityError,
    // Bays query
    availableBays,
    baysLoading,
    baysAreStale,
    baysError,
    selectedBay,
    // Derived
    isMember,
    userCanBook,
    operatingHours,
    baysForSelector,
    startTimeISO,
    endTimeISO,
    getMinDate,
    getMaxDate,
    // Standard-only extras (neutral under flow: 'demo')
    activeSubscription,
    isTeamBooking,
    resolvedGuestFeeConfig,
    // Handlers
    handleDateChange,
    handleSlotsSelect,
    handleBayChange,
    handleBaySelect,
    handleDurationChange,
    handleSelectBayClick,
    handleEditTimeClick,
  };
}
